// Hook que carga los mensajes de UNA conversación (1 paciente_id) ordenados
// por created_at asc (oldest first, para chat scroll natural).
//
// Sprint 3 B3 — polling 15s + refetch on window focus. NO Supabase Realtime
// (decisión Q4: piloto con polling alcanza).

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../client";

export type MensajeSender = "kine" | "paciente";

export interface MensajeRow {
  id: string;
  paciente_id: string;
  sender: MensajeSender;
  content: string;
  created_at: string;
  read_at: string | null;
}

interface State {
  data: MensajeRow[] | null;
  error: string | null;
  loading: boolean;
}

export interface UseMensajesConversacionReturn extends State {
  refetch: () => Promise<void>;
}

const POLL_MS = 15_000;

export function useMensajesConversacion(
  pacienteId: string | null | undefined,
): UseMensajesConversacionReturn {
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: true,
  });

  const fetchAll = useCallback(
    async (signal?: { cancelled: boolean }) => {
      if (!pacienteId) return;
      const { data, error } = await supabase
        .from("mensajes")
        .select("id, paciente_id, sender, content, created_at, read_at")
        .eq("paciente_id", pacienteId)
        .order("created_at", { ascending: true })
        .returns<MensajeRow[]>();

      if (signal?.cancelled) return;

      if (error) {
        console.warn("[useMensajesConversacion] fetch error", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }
      setState({ data: data ?? [], error: null, loading: false });
    },
    [pacienteId],
  );

  // Initial fetch + polling + window-focus refetch.
  useEffect(() => {
    if (!pacienteId) return;
    const signal = { cancelled: false };
    setState((s) => ({ ...s, loading: true }));
    void fetchAll(signal);

    const intervalId = setInterval(() => {
      void fetchAll(signal);
    }, POLL_MS);

    const onFocus = () => {
      void fetchAll(signal);
    };
    window.addEventListener("focus", onFocus);

    return () => {
      signal.cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [pacienteId, fetchAll]);

  const refetch = useCallback(async () => {
    if (!pacienteId) return;
    await fetchAll();
  }, [pacienteId, fetchAll]);

  return { ...state, refetch };
}
